<template>
  <b-modal
    ref="price-history-modal"
    hide-footer
    title="Lịch sử thay đổi giá"
    size="xl"
  >
    <div v-if="loading" class="d-flex justify-content-center py-5">
      <div class="spinner spinner-primary"></div>
    </div>

    <div v-else-if="historyItems.length === 0" class="text-center py-5">
      <p>Chưa có lịch sử thay đổi giá</p>
    </div>

    <div v-else>
      <b-row class="mb-3">
        <b-col cols="12" md="3">
          <b-form-group label="Lọc theo hành động:" label-size="sm">
            <b-form-select
              v-model="selectedAction"
              :options="actionOptions"
              size="sm"
              @change="onFilterChange"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-table
        :fields="historyFields"
        :items="historyItems"
        bordered
        hover
        small
      >
        <template v-slot:cell(stt)="row">
          {{ (currentPage - 1) * pagination.limit + row.index + 1 }}
        </template>

        <template v-slot:cell(action)="row">
          <b-badge :variant="getActionVariant(row.item.action)">
            {{ getActionLabel(row.item.action) }}
          </b-badge>
        </template>

        <template v-slot:cell(store)="row">
          <div v-if="row.item.store">
            {{ row.item.store.name }}
          </div>
          <span v-else class="text-muted">-</span>
        </template>

        <template v-slot:cell(beforeData)="row">
          <div v-if="row.item.beforeData">
            <div v-if="row.item.beforeData.sellingPrice !== undefined">
              Bán lẻ: {{ formatCurrency(row.item.beforeData.sellingPrice) }}
            </div>
            <div v-if="row.item.beforeData.wholesalePrice !== undefined">
              Bán buôn: {{ formatCurrency(row.item.beforeData.wholesalePrice) }}
            </div>
            <div v-if="row.item.beforeData.listedPrice !== undefined">
              Niêm yết: {{ formatCurrency(row.item.beforeData.listedPrice) }}
            </div>
            <div
              v-if="row.item.beforeData.updateChildProducts !== undefined"
              class="mt-1"
            >
              <small class="d-flex align-items-center" style="gap: 4px;">
                <i
                  :class="
                    row.item.beforeData.updateChildProducts
                      ? 'fas fa-check text-success'
                      : 'fas fa-times text-muted'
                  "
                ></i>
                Cập nhật SP con
              </small>
            </div>
          </div>
          <span v-else class="text-muted">-</span>
        </template>

        <template v-slot:cell(afterData)="row">
          <div v-if="row.item.afterData">
            <div v-if="row.item.afterData.sellingPrice !== undefined">
              Bán lẻ: {{ formatCurrency(row.item.afterData.sellingPrice) }}
            </div>
            <div v-if="row.item.afterData.wholesalePrice !== undefined">
              Bán buôn: {{ formatCurrency(row.item.afterData.wholesalePrice) }}
            </div>
            <div v-if="row.item.afterData.listedPrice !== undefined">
              Niêm yết: {{ formatCurrency(row.item.afterData.listedPrice) }}
            </div>
            <div
              v-if="row.item.afterData.updateChildProducts !== undefined"
              class="mt-1"
            >
              <small class="d-flex align-items-center" style="gap: 4px;">
                <i
                  :class="
                    row.item.afterData.updateChildProducts
                      ? 'fas fa-check text-success'
                      : 'fas fa-times text-muted'
                  "
                ></i>
                Cập nhật SP con
              </small>
            </div>
          </div>
          <span v-else class="text-muted">-</span>
        </template>

        <template v-slot:cell(performer)="row">
          <div v-if="row.item.performer">
            <div>{{ row.item.performer.name }}</div>
            <small class="text-muted">{{ row.item.performer.email }}</small>
          </div>
          <span v-else class="text-muted">Hệ thống</span>
        </template>

        <template v-slot:cell(createdAt)="row">
          <div>{{ formatDate(row.item.createdAt) }}</div>
          <small class="text-muted">{{ formatTime(row.item.createdAt) }}</small>
        </template>

        <template v-slot:cell(description)="row">
          {{ row.item.description || '-' }}
        </template>
      </b-table>

      <div v-if="pagination.totalPages > 1" class="mt-3">
        <b-pagination-nav-custom
          :current-page="currentPage"
          :number-of-pages="pagination.totalPages"
          :total-page="pagination.totalPages"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import { ProductStorePricingService } from '../../services/ProductStorePricingService';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import { makeToastFaile } from '@/utils/common';
import { convertPrice } from '@/utils/common';

export default {
  name: 'PriceHistoryModal',

  components: {
    BPaginationNavCustom,
  },

  data() {
    return {
      historyItems: [],
      loading: false,
      selectedStoreId: null,
      selectedStoreName: '',
      selectedProductId: null,
      selectedAction: null,
      currentPage: 1,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1,
      },
      historyFields: [
        {
          key: 'stt',
          label: 'STT',
          class: 'text-center',
          thStyle: { width: '60px' },
        },
        {
          key: 'action',
          label: 'Hành động',
          class: 'text-center',
          thStyle: { width: '120px' },
        },
        { key: 'store', label: 'Cửa hàng', thStyle: { width: '150px' } },
        { key: 'beforeData', label: 'Trước cập nhật' },
        { key: 'afterData', label: 'Sau cập nhật' },
        { key: 'performer', label: 'Người thực hiện' },
        { key: 'createdAt', label: 'Thời gian' },
        { key: 'description', label: 'Ghi chú' },
      ],
      actionOptions: [
        { value: null, text: 'Tất cả' },
        { value: 'CREATE', text: 'Tạo mới' },
        { value: 'UPDATE', text: 'Cập nhật' },
        { value: 'DELETE', text: 'Xóa' },
      ],
    };
  },

  computed: {},

  methods: {
    async show(productId, storeId, storeName) {
      this.selectedProductId = productId;
      this.selectedStoreId = storeId;
      this.selectedStoreName = storeName;
      this.currentPage = 1;

      this.$refs['price-history-modal'].show();
      await this.fetchHistory();
    },

    hide() {
      this.$refs['price-history-modal'].hide();
      this.resetData();
    },

    async fetchHistory() {
      if (!this.selectedProductId || !this.selectedStoreId) return;

      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.pagination.limit,
        };

        if (this.selectedAction) {
          params.action = this.selectedAction;
        }

        const response = await ProductStorePricingService.getPriceHistory(
          this.selectedProductId,
          params,
        );

        if (response.data) {
          this.historyItems = response.data.items || [];
          this.pagination = response.data.pagination || this.pagination;
        }
      } catch (error) {
        makeToastFaile(
          error?.data?.message || 'Lỗi khi tải lịch sử thay đổi giá',
        );
        this.historyItems = [];
      } finally {
        this.loading = false;
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchHistory();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.fetchHistory();
    },

    resetData() {
      this.historyItems = [];
      this.selectedStoreId = null;
      this.selectedStoreName = '';
      this.selectedProductId = null;
      this.selectedAction = null;
      this.currentPage = 1;
    },

    getActionLabel(action) {
      const labels = {
        CREATE: 'Tạo mới',
        UPDATE: 'Cập nhật',
        DELETE: 'Xóa',
      };
      return labels[action] || action;
    },

    getActionVariant(action) {
      const variants = {
        CREATE: 'success',
        UPDATE: 'primary',
        DELETE: 'danger',
      };
      return variants[action] || 'secondary';
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return '-';
      return convertPrice(value);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },

    formatTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.font-weight-bold {
  font-weight: 600;
}
</style>
